import React, { useCallback, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import GridViewIcon from "@mui/icons-material/GridView";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ViewAgendaOutlinedIcon from "@mui/icons-material/ViewAgendaOutlined";
import {
  Button,
  Chip,
  Divider,
  DividerProps,
  IconButton,
  IconProps,
  Stack,
  Tab,
  Tabs,
  Typography
} from "@mui/material";
import { RoleComponentGuard } from "components/guards";
import { DealCreate } from "components/modals";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useDevice, useModals, useNavigate, usePrompt } from "hooks";
import { usePickUserPermissions } from "store";

import { FreemiumMessages } from "../../../constants";
import { RoleName } from "../../../constants";
import { CardView } from "./Card-view/CardView";
import { PipelineView } from "./Pipeline-view/PipelineView";

type ViewType = "pipeline" | "card";

const dividerProps: DividerProps = {
  orientation: "vertical",
  sx: { width: "1px", height: "16px", bgcolor: "#DBE8F5", borderColor: "#DBE8F5" }
};

const iconsSx: IconProps["sx"] = {
  fontSize: "16px",
  mr: "6px"
};

export const Deals: React.FC = () => {
  const [view, setView] = useState<ViewType>("pipeline");
  const openPrompt = usePrompt();
  const { isMobile } = useDevice();
  const { infoModal } = useModals();
  const hostname = window.location.hostname;

  const myDeals = usePickUserPermissions("deals");
  const navigate = useNavigate();
  const isViewerPermissionDeals = myDeals && myDeals === "viewer";

  const createDeal = async (stageId?: number) => {
    const data = await openPrompt({ Component: DealCreate, props: { stageId } });

    if (!data) return;

    navigate({ route: "deal", params: { deal_id: data.id } });
  };

  const goToMyDeals = useCallback(() => {
    isMobile &&
      infoModal({
        title: FreemiumMessages.BEST_PRACTICE_RECOMMENDATION_TITLE,
        msg: FreemiumMessages.BEST_PRACTICE_RECOMMENDATION_MESSAGE,
        buttonText: "Continue"
      });
    navigate({ route: "myDeals" });
  }, [navigate, infoModal]);

  return (
    <Stack
      sx={{
        width: "100%",
        p: "16px",
        gap: "16px",
        bgcolor: "white",
        borderRadius: "8px",
        boxShadow: "0px 1px 2px 0px #1212171A",
        maxHeight: isMobile ? "580px" : "342px"
      }}
      alignItems="stretch"
      direction="column"
    >
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center" gap="10px">
          <Typography sx={{ fontSize: "18px", fontWeight: 500, color: "#0a3142" }}>Deals</Typography>
          <IconButton sx={{ width: "20px", height: "20px", padding: 0 }} onClick={goToMyDeals}>
            <OpenInNewIcon sx={{ width: "16px", height: "16px", color: "#071F35" }} />
          </IconButton>
        </Stack>
        <Stack direction="row" gap="12px" alignItems="center">
          <MaterialTooltip
            isShow
            title={`To create a new deal send an email to newdeal@${hostname}`}
            placement="bottom"
          >
            <Chip
              icon={<EmailOutlinedIcon sx={{ width: "16px", height: "16px", color: "#071F35" }} />}
              variant="outlined"
              sx={{
                textTransform: "none",
                border: "none",
                backgroundColor: "#f5f6f7",
                height: "24px"
              }}
              label="Add Through Email"
            />
          </MaterialTooltip>
          <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]} hideChild={isViewerPermissionDeals}>
            {isMobile ? (
              <IconButton
                onClick={() => createDeal()}
                sx={{
                  border: "1px solid #7B92A9",
                  minHeight: "24px",
                  height: "24px",
                  width: "24px",
                  minWidth: "24px",
                  borderRadius: "50%",
                  padding: "4px"
                }}
              >
                <AddCircleOutlineOutlinedIcon sx={{ width: "16px", height: "16px" }} />
              </IconButton>
            ) : (
              <Button
                startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
                variant="outlined"
                onClick={() => createDeal()}
                sx={{
                  textTransform: "none",
                  fontSize: "12px",
                  padding: "4px 8px",
                  minWidth: "auto",
                  minHeight: "24px",
                  height: "24px",
                  borderColor: "#7b92a9",
                  color: "#071f35",
                  "&:hover": {
                    borderColor: "#7b92a9",
                    backgroundColor: "transparent"
                  }
                }}
              >
                Add Deal
              </Button>
            )}
          </RoleComponentGuard>
        </Stack>
      </Stack>

      {/* View Switcher */}
      <Tabs
        classes={{
          indicator: "indicator"
        }}
        TabIndicatorProps={{
          children: <span />
        }}
        sx={{
          minHeight: "24px",
          button: {
            p: 0,
            minHeight: "20px",
            fontWeight: "normal",
            color: "#5d7782",
            fontSize: "15px"
          },
          ".MuiTabs-flexContainer": {
            alignItems: "center",
            gap: "15px"
          },
          "& .indicator": {
            display: "flex",
            justifyContent: "center",
            backgroundColor: "transparent",
            "& > span": {
              width: "98%",
              backgroundColor: "#0a3142"
            }
          }
        }}
        value={view}
        onChange={(_: React.SyntheticEvent, newValue: ViewType) => setView(newValue)}
      >
        <Tab
          label="Pipeline View"
          icon={<ViewAgendaOutlinedIcon sx={{ ...iconsSx, transform: "rotate(90deg)" }} />}
          value="pipeline"
          iconPosition="start"
        />
        <Divider {...dividerProps} />
        <Tab label="Card View" icon={<GridViewIcon sx={iconsSx} />} value="card" iconPosition="start" />
      </Tabs>

      {view === "pipeline" && <PipelineView />}
      {view === "card" && <CardView />}
    </Stack>
  );
};
